import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as Sentry from "@sentry/nextjs";
import { AnlaysisMetric, Chapter, Work, WorkAnalysis } from "@/app/types/book";
import { LLMMessage } from "@/app/lib/llm/types";
import { BackgroundTask } from "@/app/types/tasks";
import {
  IssuePriority,
  RoadmapSummaryByChapter,
  RoadmapSummaryOverall,
  Issue,
} from "@/app/types/roadmapAnalysis";
import {
  parseImpactScoreFromPriority,
  parseIssuePriority,
} from "@/app/lib/parserUtils";
import { createFriendlyName } from "@/app/lib/conversionUtils";
import { Product, ProductWithTaskStatus, Order, OrderWithProduct } from "@/app/types/products";
import { PaymentTier } from "@/app/types/payment";
import { SiteNotification } from "@/app/types/siteNotification";
import { FEATURES, DEMO_CONFIG } from "@/app/lib/config";

const API_BASE_URL = "/api";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  command?: {
    text: string;
    color: string;
  };
}

// API Error handling utility
const handleApiError = async (response: Response): Promise<void> => {
  if (response.ok) return;

  // Try to parse the error message from the response
  try {
    const data = await response.clone().json();
    if (data && data.error) {
      throw new Error(data.error);
    }
  } catch (parseError) {
    // If it's not a JSON parsing error, rethrow it
    if (parseError instanceof Error && parseError.message !== "Unexpected end of JSON input") {
      throw parseError;
    }
    // For JSON parsing errors, continue with standard error handling
  }

  // Special case for 403 - User not approved yet
  if (response.status === 403) {
    throw new Error(
      "You don't have permission to access this resource. Please contact an administrator if you believe this is an error."
    );
  } else if (response.status === 401) {
    throw new Error(
      "You need to be logged in to access this resource. Please sign in and try again."
    );
  } else if (response.status === 404) {
    throw new Error("The requested resource was not found.");
  } else {
    throw new Error(`Request failed with status: ${response.status}`);
  }
};

export interface BookState {
  // Works state
  works: Work[];
  worksLoading: boolean;
  worksError: string | null;

  // Current work state
  currentWorkId: string | null;
  currentWork: Work | null;
  workLoading: boolean;
  workError: string | null;

  // Analysis state
  analyses: WorkAnalysis[];
  currentAnalysis: WorkAnalysis | null;
  analysisLoading: boolean;
  currentAnalysisLoading: boolean;
  analysisError: string | null;
  analysisKeys: Record<string, Record<string, AnlaysisMetric>>;

  // Chat state
  messages: ChatMessage[];
  chatLoading: boolean;
  chatError: string | null;

  // Prompts state
  promptKeys: string[];
  promptContent: string | null;
  promptsLoading: boolean;
  promptContentLoading: boolean;
  promptError: string | null;

  // Products state
  products: ProductWithTaskStatus[];
  productsLoading: boolean;
  productsError: string | null;
  productsCache: Map<string, { products: ProductWithTaskStatus[], timestamp: number }>;
  analysisCache: Map<string, { analysis: WorkAnalysis, timestamp: number }>;
  roadmapSummaryCache: Map<string, { summary: RoadmapSummaryOverall, timestamp: number }>;
  roadmapChapterCache: Map<string, { chapters: Record<string, RoadmapSummaryByChapter>, timestamp: number }>;

  // Site notifications
  siteNotifications: SiteNotification[];
  siteNotificationsLoading: boolean;
  siteNotificationsError: string | null;

  // Prefetched demo book id for marketing pages
  demoWorkId: string | null;

  // Actions
  fetchWorks: () => Promise<void>;
  loadWork: (workFriendlyId: string) => Promise<void>;

  // Analysis actions
  loadAnalysis: (workFriendlyId: string, analysisId: string) => Promise<any>;
  setAnalysis: (analysis: WorkAnalysis | null) => void;
  loadAnalysisKeys: (workFriendlyId: string) => Promise<void>;
  saveAnlaysis: (
    workFriendlyId: string,
    analysisType: string,
    testResult: any
  ) => Promise<void>;

  // Chat actions
  sendMessage: (
    workFriendlyId: string,
    message: string,
    command?: string,
    history?: LLMMessage[]
  ) => Promise<LLMMessage[]>;
  clearChat: () => void;

  // Prompts actions
  fetchPromptKeys: () => Promise<void>;
  fetchPromptContent: (key: string) => Promise<void>;
  savePromptContent: (key: string, content: string) => Promise<void>;
  parsePromptTokens: (content: string) => Promise<string>;
  validateManuscript: (
    base64: string,
    mimeType: string,
    paymentTier: PaymentTier
  ) => Promise<any>;
  testPrompt: (workId: string, prompt: string, model?: string, productCodeName?: string) => Promise<any>;
  calculateScore: (workFriendlyId: string, promptKey: string) => Promise<any>;

  // Character actions
  loadCharacter: (workFriendlyId: string, characterId: string) => Promise<any>;

  // Chapter actions
  loadChapter: (workFriendlyId: string, chapterId: string) => Promise<any>;

  // Storyflow actions
  loadStoryflow: (workFriendlyId: string) => Promise<Chapter[]>;

  loadRoadmapAnalysis: (
    workFriendlyId: string
  ) => Promise<RoadmapSummaryOverall>;
  loadRoadmapAnalysisByChapter: (
    workFriendlyId: string
  ) => Promise<Record<string, RoadmapSummaryByChapter>>;

  // Products actions
  loadProducts: () => Promise<Product[]>;
  getProductsForWork: (workFriendlyId: string) => Promise<ProductWithTaskStatus[]>;
  invalidateProductsCache: (workFriendlyId?: string) => void;
  purchaseProduct: (workFriendlyId: string, productId: string) => Promise<any>;
  invalidateWorkDataCache: (workFriendlyId: string) => void;
  getUnassociatedOrder: () => Promise<OrderWithProduct | null>;
  loadSiteNotifications: () => Promise<void>;

  // Prefetch the public demo book to improve marketing page speed
  prefetchDemoWork: () => Promise<string | null>;

  // --- NEW State for Combined Admin Prompts ---
  availablePrompts: PromptIdentifier[];
  availablePromptsLoading: boolean;
  availablePromptsError: string | null;
  currentPrompt: CurrentPromptState | null;
  // --- END NEW State ---

  // --- NEW Actions for Combined Admin Prompts ---
  loadAvailablePrompts: () => Promise<void>;
  loadPromptContent: (id: string, type: 'product' | 'genre') => Promise<void>;
  saveCurrentPromptContent: (newValue: string) => Promise<void>;
  clearCurrentPrompt: () => void;
  // --- END NEW Actions ---
}

interface PromptIdentifier {
  id: string;
  name: string;
  type: "product" | "genre";
}

interface CurrentPromptState {
  key: string;
  value: string;
  version: number | null;
  isLoading: boolean;
  error: string | null;
}

const requestCache = new Map<string, Promise<any>>();

// Function to clear cache entry after a delay
const clearCacheAfterDelay = (key: string) => {
  setTimeout(() => {
    requestCache.delete(key);
  }, 5000); // Clear cache after 5 seconds
};

// Helper function to parse string priority to IssuePriority enum
const parsePriority = (priority: string): IssuePriority => {
  switch (priority) {
    case "High":
      return IssuePriority.High;
    case "Medium":
      return IssuePriority.Medium;
    case "Low":
      return IssuePriority.Low;
    default:
      return IssuePriority.NA;
  }
};

// Helper function to parse issues and convert string priorities to enum values
const parseIssues = (issues: any[]): Issue[] => {
  if (!issues || !Array.isArray(issues)) {
    return [];
  }

  return issues.map((issue) => {
    // Handle implementation ideas which can be either strings or objects
    let implementationIdeas = issue.implementation_ideas;

    // Ensure implementation_ideas is always an array
    if (!implementationIdeas) {
      implementationIdeas = [];
    } else if (!Array.isArray(implementationIdeas)) {
      implementationIdeas = [implementationIdeas];
    }

    return {
      ...issue,
      priority: parsePriority(issue.priority),
      implementation_ideas: implementationIdeas,
    };
  });
};

export const useBookStore = create<BookState>()(
  persist(
    (set, get) => ({
  // Initial state
  works: [],
  worksLoading: false,
  worksError: null,
  currentWorkId: null,
  currentWork: null,
  workLoading: false,
  workError: null,
  analyses: [],
  currentAnalysis: null,
  analysisLoading: false,
  currentAnalysisLoading: false,
  analysisError: null,
  analysisKeys: {},
  messages: [],
  chatLoading: false,
  chatError: null,
  promptKeys: [],
  promptContent: null,
  promptsLoading: false,
  promptContentLoading: false,
  promptError: null,

  // Products state
  products: [],
  productsLoading: false,
  productsError: null,
  productsCache: new Map(),
  analysisCache: new Map(),
  roadmapSummaryCache: new Map(),
  roadmapChapterCache: new Map(),
  siteNotifications: [],
  siteNotificationsLoading: false,
  siteNotificationsError: null,

  demoWorkId: null,

  // --- NEW State Initialization ---
  availablePrompts: [],
  availablePromptsLoading: false,
  availablePromptsError: null,
  currentPrompt: null,
  // --- END NEW State Initialization ---

  // Load a single work by ID
  loadWork: async (workId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading work: ${workId}`,
      level: 'info',
    });
    const state = get();

    // Skip if already loaded and matches current ID
    if (state.currentWork?.friendly_id === workId && !state.workLoading) {
      return;
    }

    // Use cached request if exists
    const cacheKey = `work-${workId}`;
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    set({ workLoading: true, workError: null });

    const demoPath =
      FEATURES.PUBLIC_DEMO_WORKS && DEMO_CONFIG.isValidDemoId(workId)
        ? `/demo/books/${workId}`
        : `/books/${workId}`;

    const request = fetch(`${API_BASE_URL}${demoPath}`)
      .then(async (res) => {
        try {
          await handleApiError(res);
        } catch (error) {
          // Check if this is our special NOT_APPROVED error
          if (error instanceof Error && error.message === "NOT_APPROVED") {
            set({
              workError: "NOT_APPROVED",
              workLoading: false,
            });
            throw new Error("NOT_APPROVED");
          }
          // Re-throw other errors
          throw error;
        }
        return res.json();
      })
      .then((work) => {
        set({
          currentWorkId: workId,
          currentWork: work,
          workLoading: false,
        });
        return work;
      })
      .catch((error) => {
        console.error("Error loading work:", error);
        // Only set error if it's not our special NOT_APPROVED error (which is already handled)
        if (error.message !== "NOT_APPROVED") {
          set({
            workError: error instanceof Error ? error.message : "Unknown error",
            workLoading: false,
          });
        }
        Sentry.captureException(error, {
          tags: {
            workFriendlyId: workId,
            storeAction: "loadWork",
          },
        });
        throw error;
      })
      .finally(() => {
        clearCacheAfterDelay(cacheKey);
      });

    requestCache.set(cacheKey, request);
    return request;
  },

  // Fetch all works for the user
  fetchWorks: async () => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Fetching all works',
      level: 'info',
    });
    const currentState = get();

    // Prevent multiple simultaneous fetches
    if (currentState.worksLoading) {
      return;
    }

    set({ worksLoading: true, worksError: null });

    try {
      const response = await fetch(`${API_BASE_URL}/books`, {
        method: "GET",
        credentials: "include",
      });

      try {
        await handleApiError(response);
      } catch (error) {
        // Check if this is our special NOT_APPROVED error
        if (error instanceof Error && error.message === "NOT_APPROVED") {
          set({
            worksError: "NOT_APPROVED",
            worksLoading: false,
          });
          return;
        }
        // Re-throw other errors to be caught by the outer catch block
        throw error;
      }

      const works = await response.json();
      set({ works, worksLoading: false });
    } catch (error) {
      console.error("Error fetching works:", error);
      Sentry.captureException(error, {
        tags: {
          storeAction: "fetchWorks",
        },
      });
      set({
        worksError: error instanceof Error ? error.message : "Unknown error",
        worksLoading: false,
      });
    }
  },

  // Load analysis by ID with 5-minute cache
  loadAnalysis: async (workFriendlyId: string, analysisType: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading analysis for work ${workFriendlyId} and type ${analysisType}`,
      level: 'info',
    });
    const { analyses, analysisCache } = get();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();

    const cacheMapKey = `${workFriendlyId}-${decodeURIComponent(analysisType)}`;
    const cachedItem = analysisCache.get(cacheMapKey);
    if (cachedItem && (now - cachedItem.timestamp) < CACHE_TTL) {
      set({ currentAnalysis: cachedItem.analysis });
      return Promise.resolve(cachedItem.analysis);
    }
    const decodedAnalysisType = decodeURIComponent(analysisType);

    // Check if we already have this analysis in the store
    const existingAnalysis = analyses.find(
      (a) => a.analysis_type === decodedAnalysisType
    );
    if (existingAnalysis) {
      set({ currentAnalysis: existingAnalysis });
      return;
    }

    // Use cached request if exists
    const cacheKey = `analysis-${workFriendlyId}-${decodedAnalysisType}`;
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    set({ currentAnalysisLoading: true, analysisError: null });

    const request = fetch(
      `${API_BASE_URL}/books/${workFriendlyId}/analysis/${decodedAnalysisType}`,
      {
        method: "GET",
        credentials: "include",
      }
    )
      .then((response) => {
        handleApiError(response);
        return response.json();
      })
      .then((analysis) => {
        const latestState = get();
        // Update analyses list but avoid duplicates
        const newAnalyses = [...latestState.analyses.filter(a => a.analysis_type !== analysis.analysis_type), analysis];

        set({
          analyses: newAnalyses as WorkAnalysis[],
          currentAnalysis: analysis,
          currentAnalysisLoading: false,
          analysisCache: new Map(latestState.analysisCache).set(cacheMapKey, {
            analysis,
            timestamp: now,
          }),
        });
        clearCacheAfterDelay(cacheKey);
        return analysis;
      })
      .catch((error) => {
        console.error(error);
        set({
          analysisError:
            error instanceof Error ? error.message : "Unknown error",
          currentAnalysisLoading: false,
        });
        Sentry.captureException(error, {
          tags: {
            workFriendlyId: workFriendlyId,
            analysisType: analysisType,
            storeAction: "loadAnalysis",
          },
        });
        clearCacheAfterDelay(cacheKey);
        throw error;
      });

    requestCache.set(cacheKey, request);
    return request;
  },

  loadAnalysisKeys: async (workId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading analysis keys for work ${workId}`,
      level: 'info',
    });
    const state = get();

    // Skip if already loaded
    if (Object.keys(state.analysisKeys).length > 0 && !state.analysisLoading) {
      return;
    }

    // Use cached request if exists
    const cacheKey = `analysis-keys-${workId}`;
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    set({ analysisLoading: true, analysisError: null });

    const analysisPath =
      FEATURES.PUBLIC_DEMO_WORKS && DEMO_CONFIG.isValidDemoId(workId)
        ? `/demo/books/${workId}/analyses?keys=true`
        : `/books/${workId}/analyses?keys=true`;

    const request = fetch(`${API_BASE_URL}${analysisPath}`)
      .then((res) => {
        handleApiError(res);
        return res.json();
      })
      .then((keys) => {
        const keysTyped = keys as Record<
          string,
          Record<string, AnlaysisMetric>
        >;

        // ensure all metric.value are parsed into IssuePriority
        for (const analysisName of Object.keys(keysTyped)) {
          if (!keysTyped[analysisName]) {
            continue;
          }
          for (const metricName of Object.keys(keysTyped[analysisName])) {
            const metric = keysTyped[analysisName][metricName];
            metric.value = parseIssuePriority(String(metric.value));
            metric.impactScore = parseImpactScoreFromPriority(
              String(metric.value)
            );
          }
        }

        set({
          analysisKeys: keys,
          analysisLoading: false,
        });
        clearCacheAfterDelay(cacheKey);
        return keys;
      })
      .catch((error) => {
        set({ analysisLoading: false, analysisError: error.message });
        Sentry.captureException(error, {
          tags: {
            workFriendlyId: workId,
            storeAction: "loadAnalysisKeys",
          },
        });
        clearCacheAfterDelay(cacheKey);
        throw error;
      });

    requestCache.set(cacheKey, request);
    return request;
  },

  saveAnlaysis: async (
    workFriendlyId: string,
    analysisType: string,
    testResult: any
  ) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Saving analysis for work ${workFriendlyId} and type ${analysisType}`,
      level: 'info',
    });
    const response = await fetch(
      `${API_BASE_URL}/books/${workFriendlyId}/analysis/${analysisType}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: testResult,
      }
    );
    handleApiError(response);
    const result = await response.json();
    return result;
  },

  // Set current analysis
  setAnalysis: (analysis: WorkAnalysis | null) => {
    set({ currentAnalysis: analysis });
  },

  // Chat actions
  sendMessage: async (
    workId: string,
    message: string,
    command?: string,
    history: LLMMessage[] = []
  ) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Sending message for work ${workId}`,
      level: 'info',
    });
    const currentState = get();

    // Prevent multiple simultaneous chat requests
    if (currentState.chatLoading) {
      return [];
    }

    set((state) => ({
      messages: [
        ...state.messages,
        { role: "user" as const, content: message },
      ],
      chatLoading: true,
      chatError: null,
    }));

    try {
      let response: Response;
      if (command) {
        // Call the API route /api/book/[id]/chat
        response = await fetch(`${API_BASE_URL}/books/${workId}/chat`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ workId, message, command, history }),
        });
        handleApiError(response);
        const responseText = await response.text();

        // Check if the chat is still active
        const latestState = get();
        if (latestState.chatLoading) {
          set((state) => ({
            messages: [
              ...state.messages,
              {
                role: "assistant" as const,
                content: responseText,
              },
            ],
            chatLoading: false,
          }));
          return [
            ...history,
            { role: "user", content: message },
            { role: "assistant", content: responseText },
          ];
        }
      } else {
        response = await fetch(`${API_BASE_URL}/books/${workId}/chat`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ workId, message, history }),
        });
        handleApiError(response);
        const responseText = await response.text();

        // Check if the chat is still active
        const latestState = get();
        if (latestState.chatLoading) {
          set((state) => ({
            messages: [
              ...state.messages,
              { role: "assistant" as const, content: responseText },
            ],
            chatLoading: false,
          }));
          return [
            ...history,
            { role: "user", content: message },
            { role: "assistant", content: responseText },
          ];
        }
      }
      return history;
    } catch (error) {
      set(() => ({
        chatError: error instanceof Error ? error.message : "An error occurred",
        chatLoading: false,
      }));
      Sentry.captureException(error, {
        tags: {
          workFriendlyId: workId,
          storeAction: "sendMessage",
        },
      });
      throw error;
    }
  },

  clearChat: () => {
    set({ messages: [] });
  },

  // Prompts actions
  fetchPromptKeys: async () => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Fetching prompt keys',
      level: 'info',
    });
    set({ promptsLoading: true, promptError: null });
    try {
      const response = await fetch(`${API_BASE_URL}/admin/promptKeys`, {
        method: "GET",
        credentials: "include",
      });
      handleApiError(response);
      const keys = await response.json();
      set({ promptKeys: keys });
    } catch (error) {
      set({ promptError: (error as Error).message });
      Sentry.captureException(error, {
        tags: {
          storeAction: "fetchPromptKeys",
        },
      });
    } finally {
      set({ promptsLoading: false });
    }
  },

  fetchPromptContent: async (key: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Fetching prompt content for key ${key}`,
      level: 'info',
    });
    set({ promptContentLoading: true, promptError: null });
    try {
      const response = await fetch(`${API_BASE_URL}/admin/prompt/${key}`, {
        method: "GET",
        credentials: "include",
      });
      handleApiError(response);
      const content = await response.json();
      set({ promptContent: content.value });
    } catch (error) {
      set({ promptError: (error as Error).message });
      Sentry.captureException(error, {
        tags: {
          promptKey: key,
          storeAction: "fetchPromptContent",
        },
      });
    } finally {
      set({ promptContentLoading: false });
    }
  },

  savePromptContent: async (key: string, value: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Saving prompt content for key ${key}`,
      level: 'info',
    });
    set({ promptContentLoading: true, promptError: null });
    try {
      const response = await fetch(`${API_BASE_URL}/admin/prompt/${key}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ value }),
      });
      handleApiError(response);
      const newValue = await response.json();
      set({ promptContent: newValue.value });
    } catch (error) {
      set({ promptError: (error as Error).message });
      Sentry.captureException(error, {
        tags: {
          promptKey: key,
          storeAction: "savePromptContent",
        },
      });
    } finally {
      set({ promptContentLoading: false });
    }
  },

  parsePromptTokens: async (content: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Parsing prompt tokens',
      level: 'info',
    });
    try {
      const response = await fetch(`${API_BASE_URL}/admin/parseTokens`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      handleApiError(response);
      const tokens = await response.json();
      return tokens;
    } catch (error) {
      console.error("Failed to parse tokens:", error);
      Sentry.captureException(error, {
        tags: {
          storeAction: "parsePromptTokens",
        },
      });
      return content;
    }
  },

  validateManuscript: async (
    base64: string,
    mimeType: string,
    paymentTier: PaymentTier
  ) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Validating manuscript',
      level: 'info',
    });
    try {
      const response = await fetch(`${API_BASE_URL}/validate-manuscript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ base64, mimeType, paymentTier }),
      });
      handleApiError(response);
      const result = await response.json();
      if (!result.valid) {
        Sentry.captureMessage('Manuscript validation failed', {
          level: 'warning',
          extra: { mimeType, paymentTier, ...result },
        });
      }
      return result;
    } catch (error) {
      console.error('Failed to validate manuscript:', error);
      Sentry.captureException(error, {
        tags: { storeAction: 'validateManuscript' },
      });
      throw error;
    }
  },

  testPrompt: async (workId: string, prompt: string, model?: string, productCodeName?: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Testing prompt for work ${workId}`,
      level: 'info',
    });
    try {
      // Start the background task
      const response = await fetch(
        `${API_BASE_URL}/tasks/internal/test-prompt`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ workId, prompt, model, productCodeName }),
        }
      );
      handleApiError(response);
      const { taskId } = await response.json();

      // Poll for task completion
      const maxAttempts = 60; // 5 minutes with 5-second intervals
      const interval = 5000; // 5 seconds

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const taskResponse = await fetch(`${API_BASE_URL}/tasks/${taskId}`);
        handleApiError(taskResponse);
        const task = (await taskResponse.json()) as BackgroundTask;

        if (task.status === "completed") {
          return task.result;
        }

        if (task.status === "error") {
          throw new Error(task.error || "Task failed");
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, interval));
      }

      throw new Error("Task timed out after 5 minutes");
    } catch (error) {
      console.error("Error in testPrompt:", error);
      Sentry.captureException(error, {
        tags: {
          workFriendlyId: workId,
          storeAction: "testPrompt",
        },
      });
      throw error;
    }
  },

  loadCharacter: async (workFriendlyId: string, characterId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading character ${characterId} for work ${workFriendlyId}`,
      level: 'info',
    });
    const response = await fetch(
      `${API_BASE_URL}/books/${workFriendlyId}/character/${characterId}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    handleApiError(response);
    const result = await response.json();
    return result;
  },

  loadChapter: async (workFriendlyId: string, chapterId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading chapter ${chapterId} for work ${workFriendlyId}`,
      level: 'info',
    });
    const response = await fetch(
      `${API_BASE_URL}/books/${workFriendlyId}/chapter/${chapterId}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    handleApiError(response);
    const result = await response.json();
    return result;
  },

  loadStoryflow: async (workId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading storyflow for work ${workId}`,
      level: 'info',
    });
    const response = await fetch(`${API_BASE_URL}/books/${workId}/story-flow`, {
      method: "GET",
      credentials: "include",
    });
    handleApiError(response);
    const result = await response.json();
    return result as Chapter[];
  },
  calculateScore: async (workId: string, promptKey: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Calculating score for work ${workId} and prompt ${promptKey}`,
      level: 'info',
    });
    const response = await fetch(`${API_BASE_URL}/admin/calculate-score`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ workId, promptKey }),
    });
    handleApiError(response);
    const result = await response.json();
    return result as Chapter[];
  },

  loadRoadmapAnalysis: async (workFriendlyId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading roadmap analysis for work ${workFriendlyId}`,
      level: 'info',
    });

    const { roadmapSummaryCache } = get();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();

    // Return cached data if within TTL
    const cachedData = roadmapSummaryCache.get(workFriendlyId);
    if (cachedData && (now - cachedData.timestamp) < CACHE_TTL) {
      return Promise.resolve(cachedData.summary);
    }

    const cacheKey = `roadmap-summary-${workFriendlyId}`;
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    const summaryPath =
      FEATURES.PUBLIC_DEMO_WORKS && DEMO_CONFIG.isValidDemoId(workFriendlyId)
        ? `/demo/books/${workFriendlyId}/roadmap/summary`
        : `/books/${workFriendlyId}/roadmap/summary`;

    const request = (async () => {
      const res = await fetch(`${API_BASE_URL}${summaryPath}`, {
        method: "GET",
        credentials: "include",
      });

      await handleApiError(res);
      const result = await res.json();

      const parsedResult: RoadmapSummaryOverall = {
        ...result,
        top_areas_for_improvement: parseIssues(result.top_areas_for_improvement),
      };

      // Store in cache
      set((state) => ({
        roadmapSummaryCache: new Map(state.roadmapSummaryCache).set(workFriendlyId, {
          summary: parsedResult,
          timestamp: now,
        }),
      }));

      return parsedResult;
    })()
      .catch((error) => {
        console.error("[useBookStore.loadRoadmapAnalysis] Error:", error);
        Sentry.captureException(error, {
          tags: {
            workFriendlyId,
            storeAction: "loadRoadmapAnalysis",
          },
        });
        throw error;
      })
      .finally(() => {
        clearCacheAfterDelay(cacheKey);
      });

    requestCache.set(cacheKey, request);
    return request;
  },

  loadRoadmapAnalysisByChapter: async (workFriendlyId: string) => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading roadmap analysis by chapter for work ${workFriendlyId}`,
      level: 'info',
    });

    const { roadmapChapterCache } = get();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();

    // Return cached data if within TTL
    const cachedData = roadmapChapterCache.get(workFriendlyId);
    if (cachedData && (now - cachedData.timestamp) < CACHE_TTL) {
      return Promise.resolve(cachedData.chapters);
    }

    const cacheKey = `roadmap-chapter-${workFriendlyId}`;
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    const chapterPath =
      FEATURES.PUBLIC_DEMO_WORKS && DEMO_CONFIG.isValidDemoId(workFriendlyId)
        ? `/demo/books/${workFriendlyId}/roadmap/chapter`
        : `/books/${workFriendlyId}/roadmap/chapter`;

    const request = (async () => {
      const res = await fetch(`${API_BASE_URL}${chapterPath}`, {
        method: "GET",
        credentials: "include",
      });

      await handleApiError(res);
      const result = await res.json();

      const parsedResult: Record<string, RoadmapSummaryByChapter> = {};
      for (const chapterKey in result) {
        if (Object.prototype.hasOwnProperty.call(result, chapterKey)) {
          const chapterData = result[chapterKey];
          parsedResult[chapterKey] = {
            ...chapterData,
            friendly_id: chapterData.name
              ? createFriendlyName(chapterData.name)
              : chapterData.friendly_id,
            issues: parseIssues(chapterData.issues),
          };
        }
      }

      // Store in cache
      set((state) => ({
        roadmapChapterCache: new Map(state.roadmapChapterCache).set(workFriendlyId, {
          chapters: parsedResult,
          timestamp: now,
        }),
      }));

      return parsedResult;
    })()
      .catch((error) => {
        console.error("[useBookStore.loadRoadmapAnalysisByChapter] Error:", error);
        Sentry.captureException(error, {
          tags: {
            workFriendlyId,
            storeAction: "loadRoadmapAnalysisByChapter",
          },
        });
        throw error;
      })
      .finally(() => {
        clearCacheAfterDelay(cacheKey);
      });

    requestCache.set(cacheKey, request);
    return request;
  },

  // Get products that the user has purchased for a specific work
  async getProductsForWork(workFriendlyId: string): Promise<ProductWithTaskStatus[]> {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Getting products for work: ${workFriendlyId}`,
      level: 'info',
    });
    try {
      const { productsCache } = get();
      const cacheKey = `user-products-${workFriendlyId}`;
      const now = Date.now();
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

      // Check if we have a valid cached result in the Zustand store
      const cachedData = productsCache.get(workFriendlyId);
      if (cachedData && (now - cachedData.timestamp) < CACHE_TTL) {
        return cachedData.products;
      }

      // Check if we have a cached request in progress
      if (requestCache.has(cacheKey)) {
        return await requestCache.get(cacheKey);
      }

      // Create a new request promise
      const requestPromise = (async () => {
        const response = await fetch(`${API_BASE_URL}/books/${workFriendlyId}/products`);

        await handleApiError(response);

        const data = await response.json() as ProductWithTaskStatus[];

        // Update the Zustand store with the fetched data
        set((state) => ({
          productsCache: new Map(state.productsCache).set(workFriendlyId, {
            products: data,
            timestamp: now,
          }),
        }));

        return data;
      })();

      // Cache the request promise
      requestCache.set(cacheKey, requestPromise);

      // Clear the request cache after a delay
      clearCacheAfterDelay(cacheKey);

      // Return the result
      return await requestPromise;
    } catch (error) {
      console.error("Error fetching user products:", error);
      Sentry.captureException(error, {
        tags: {
          workFriendlyId: workFriendlyId,
          storeAction: "getProductsForWork",
        },
      });
      throw error;
    }
  },

  // Add a method to invalidate the products cache for a specific work
  invalidateProductsCache: (workFriendlyId?: string): void => {
    if (workFriendlyId) {
      // Invalidate cache for a specific work
      set((state) => {
        const newCache = new Map(state.productsCache);
        newCache.delete(workFriendlyId);
        return { productsCache: newCache };
      });
      // Also clear the request cache
      const cacheKey = `user-products-${workFriendlyId}`;
      requestCache.delete(cacheKey);
    } else {
      // Invalidate all product caches
      set({ productsCache: new Map() });
      // Clear all product-related request caches
      for (const key of requestCache.keys()) {
        if (key.startsWith("user-products-")) {
          requestCache.delete(key);
        }
      }
    }
  },

  loadProducts: async (): Promise<Product[]> => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Loading all products',
      level: 'info',
    });
    // Check if we already have products in the store
    const { products } = get();
    if (products && products.length > 0) {
      return products;
    }

    set({ productsLoading: true, productsError: null });
    try {
      const cacheKey = "products-all";

      // Check if we have a cached request
      if (requestCache.has(cacheKey)) {
        const cachedProducts = await requestCache.get(cacheKey);
        return cachedProducts;
      }

      // Create a new request
      const request = async (): Promise<Product[]> => {
        const response = await fetch(`${API_BASE_URL}/products/all`);
        await handleApiError(response);
        const data = await response.json();
        set({ products: data, productsLoading: false });
        return data;
      };

      // Store the request in cache and clear after 5 seconds
      const requestPromise = request();
      requestCache.set(cacheKey, requestPromise);
      clearCacheAfterDelay(cacheKey);

      return requestPromise;
    } catch (error) {
      console.error("[useBookStore] Error loading products:", error);
      Sentry.captureException(error, {
        tags: {
          storeAction: "loadProducts",
        },
      });
      set({
        productsError: error instanceof Error ? error.message : "Failed to fetch products",
        productsLoading: false,
      });
      return [];
    }
  },

  // Purchase a product for a specific work
  async purchaseProduct(workFriendlyId: string, productId: string): Promise<any> {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Purchasing product ${productId} for work ${workFriendlyId}`,
      level: 'info',
    });
    try {
      const response = await fetch(`${API_BASE_URL}/books/${workFriendlyId}/products/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId }),
      });

      await handleApiError(response);
      
      // Clear the cache for this work's products
      const cacheKey = `user-products-${workFriendlyId}`;
      requestCache.delete(cacheKey);
      
      // Clear the Zustand store cache for this work
      set((state) => {
        const newCache = new Map(state.productsCache);
        newCache.delete(workFriendlyId);
        return { productsCache: newCache };
      });

      return await response.json();
    } catch (error) {
      console.error("Error purchasing product:", error);
      Sentry.captureException(error, {
        tags: {
          workFriendlyId: workFriendlyId,
          productId: productId,
          storeAction: "purchaseProduct",
        },
      });
      throw error;
    }
  },


  invalidateWorkDataCache: (workFriendlyId: string): void => {
    const workCacheKey = `work-${workFriendlyId}`;
    if (requestCache.has(workCacheKey)) {
      requestCache.delete(workCacheKey);
      // console.log(`[useBookStore] Invalidated work data request cache for ${workFriendlyId}`);
    }
    // Note: loadWork does not use the Zustand-level 'productsCache', so no need to clear that here.
  },

  async getUnassociatedOrder(): Promise<OrderWithProduct | null> {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Fetching unassociated order',
      level: 'info',
    });
    try {
      const response = await fetch(`${API_BASE_URL}/orders/unassociated`, {
        method: 'GET',
        credentials: 'include',
      });

      await handleApiError(response);
      const result = (await response.json()) as
        | { order: Order; product: Product }
        | null;
      if (!result) return null;
      return { ...result.order, product: result.product } as OrderWithProduct;
    } catch (error) {
      console.error('Error fetching unassociated order:', error);
      Sentry.captureException(error, {
        tags: { storeAction: 'getUnassociatedOrder' },
      });
      return null;
    }
  },

  async loadSiteNotifications(): Promise<void> {
    try {
      set({ siteNotificationsLoading: true, siteNotificationsError: null });
      const response = await fetch(`${API_BASE_URL}/site-notifications`);
      await handleApiError(response);
      const data: SiteNotification[] = await response.json();
      set({ siteNotifications: data, siteNotificationsLoading: false });
    } catch (error) {
      console.error('Error loading site notifications:', error);
      Sentry.captureException(error, {
        tags: { storeAction: 'loadSiteNotifications' },
      });
      set({
        siteNotificationsError:
          error instanceof Error ? error.message : 'Failed to load notifications',
        siteNotificationsLoading: false,
      });
    }
  },

  async prefetchDemoWork(): Promise<string | null> {
    const cacheKey = 'prefetch-demo';
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey);
    }

    const request = (async () => {
      const { demoWorkId } = get();
      if (demoWorkId) {
        // Trigger background preloads for associated resources
        void get().loadWork(demoWorkId);
        void get().loadAnalysisKeys(demoWorkId);
        void get().loadRoadmapAnalysis(demoWorkId);
        void get().loadRoadmapAnalysisByChapter(demoWorkId);
        return demoWorkId;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/demo/books`);
        await handleApiError(response);
        const works = await response.json();
        const first = Array.isArray(works) && works.length > 0 ? works[0] : null;
        const id = first ? first.friendly_id : null;
        set({ demoWorkId: id });
        if (id) {
          // Preload related endpoints in the background
          void get().loadWork(id);
          void get().loadAnalysisKeys(id);
          void get().loadRoadmapAnalysis(id);
          void get().loadRoadmapAnalysisByChapter(id);
        }
        return id;
      } catch (error) {
        console.error('Error prefetching demo work:', error);
        Sentry.captureException(error, { tags: { storeAction: 'prefetchDemoWork' } });
        set({ demoWorkId: null });
        return null;
      }
    })().finally(() => {
      clearCacheAfterDelay(cacheKey);
    });

    requestCache.set(cacheKey, request);
    return request;
  },

  // --- NEW Actions Implementation ---

  // Load the list of available prompt identifiers (products and genres)
  loadAvailablePrompts: async () => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: 'Loading available prompts',
      level: 'info',
    });
    set({ availablePromptsLoading: true, availablePromptsError: null });
    try {
      const response = await fetch(`${API_BASE_URL}/prompts/identifiers`);
      await handleApiError(response); // Reuse existing error handler
      const data: PromptIdentifier[] = await response.json();
      set({ availablePrompts: data, availablePromptsLoading: false });
    } catch (error: any) {
      console.error("Failed to load available prompts:", error);
      Sentry.captureException(error, {
        tags: {
          storeAction: "loadAvailablePrompts",
        },
      });
      set({ availablePromptsError: error.message, availablePromptsLoading: false });
    }
  },

  // Load the content (text) for a specific prompt based on its ID and type
  loadPromptContent: async (id: string, type: 'product' | 'genre') => {
    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Loading prompt content for id: ${id}, type: ${type}`,
      level: 'info',
    });
    let key: string;
    if (type === 'product') {
      // ASSUMPTION: Product prompts use this key format
      key = `prompt:product:${id}`;
    } else if (type === 'genre') {
      key = `prompt:genre-specialist:${id}`;
    } else {
      console.error(`Unknown prompt type: ${type}`);
      set({ currentPrompt: { key: '', value: '', version: null, isLoading: false, error: 'Unknown prompt type' } });
      return;
    }

    set({ currentPrompt: { key, value: '', version: null, isLoading: true, error: null } });

    try {
      const encodedKey = encodeURIComponent(key);
      const response = await fetch(`${API_BASE_URL}/static-content/${encodedKey}`);
      await handleApiError(response); // Check for basic HTTP errors
      const data = await response.json(); // { id, value, version } or error

      if (response.ok) {
         // getStaticContentByKey returns { id, value, version }
         // Handle the case where content is not found (returns { value: "", version: 0 })
        set({ currentPrompt: { key, value: data.value, version: data.version, isLoading: false, error: null } });
      } else {
        // Handle potential errors returned in the JSON body even with non-2xx status
        const errorMessage = data.error || 'Failed to load prompt content';
        console.error(`Error loading prompt content for key ${key}:`, errorMessage);
        Sentry.captureException(new Error(errorMessage), {
          tags: {
            promptKey: key,
            storeAction: "loadPromptContent",
          },
        });
        set({ currentPrompt: { key, value: '', version: null, isLoading: false, error: errorMessage } });
      }
    } catch (error: any) {
      console.error(`Failed to load prompt content for key ${key}:`, error);
      Sentry.captureException(error, {
        tags: {
          promptKey: key,
          storeAction: "loadPromptContent",
        },
      });
      set({ currentPrompt: { key, value: '', version: null, isLoading: false, error: error.message } });
    }
  },

  // Save the content for the currently loaded prompt
  saveCurrentPromptContent: async (newValue: string) => {
    const currentPromptState = get().currentPrompt;
    if (!currentPromptState || !currentPromptState.key || currentPromptState.isLoading) {
      console.warn("Attempted to save prompt content while none is loaded, key is missing, or already saving.");
      Sentry.addBreadcrumb({
        category: 'store.action',
        message: 'Save prompt content aborted: no active prompt.',
        level: 'warning',
      });
      return;
    }

    const { key: promptKey } = currentPromptState;

    Sentry.addBreadcrumb({
      category: 'store.action',
      message: `Saving prompt content for key: ${promptKey}`,
      level: 'info',
    });

    set(state => ({
      currentPrompt: state.currentPrompt ? { ...state.currentPrompt, isLoading: true, error: null } : null
    }));

    try {
      const encodedKey = encodeURIComponent(promptKey);
      const response = await fetch(`${API_BASE_URL}/static-content/${encodedKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ value: newValue }),
      });
      await handleApiError(response);
      const data = await response.json(); // { id, value, version } or error

      if (response.ok) {
        // Update state with the newly saved value and version
        set({ currentPrompt: { key: promptKey, value: data.value, version: data.version, isLoading: false, error: null } });
      } else {
        const errorMessage = data.error || 'Failed to save prompt content';
        console.error(`Error saving prompt content for key ${promptKey}:`, errorMessage);
        Sentry.captureException(new Error(errorMessage), {
          tags: {
            promptKey: promptKey,
            storeAction: "saveCurrentPromptContent",
          },
        });
        set(state => ({
          currentPrompt: state.currentPrompt ? { ...state.currentPrompt, isLoading: false, error: errorMessage } : null
        }));
      }
    } catch (error: any) {
      console.error(`Failed to save prompt content for key ${promptKey}:`, error);
      Sentry.captureException(error, {
        tags: {
          promptKey: promptKey,
          storeAction: "saveCurrentPromptContent",
        },
      });
      set(state => ({
        currentPrompt: state.currentPrompt ? { ...state.currentPrompt, isLoading: false, error: error.message } : null
      }));
    }
  },

  // Clear the currently loaded prompt state
  clearCurrentPrompt: () => {
    set({ currentPrompt: null });
  },

  // --- END NEW Actions Implementation ---

    }),
    {
      name: "book-store",
      partialize: (state) => ({
        productsCache: Array.from(state.productsCache.entries()),
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        productsCache: new Map(
          (persistedState as any)?.productsCache ?? []
        ),
      }),
    }
  )
);

export type { ChatMessage, WorkAnalysis };
